import { useState, useEffect } from 'react'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Search,
  Package,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  MapPin,
  DollarSign,
  Truck,
  Phone,
  User,
  Eye
} from 'lucide-react'

interface Order {
  id: string
  product_id: string
  final_price: number
  status: 'pending' | 'confirmed' | 'paid' | 'delivered' | 'cancelled'
  delivery_type: 'standard' | 'express' | 'pickup'
  delivery_address: string
  confirmation_code: string | null
  created_at: string
  product: {
    title: string
    images: string[]
  }
  buyer: {
    full_name: string | null
    email: string | null
    phone_number: string | null
  } | null
}

interface DeliveryRequest {
  id: string
  order_id: string
  status: 'pending' | 'accepted' | 'completed' | 'cancelled'
  delivery_partner_id: string | null
  pickup_instructions: string | null
  delivery_notes: string | null
  created_at: string
}

export function SellerOrdersPage() {
  const user = useAuthStore((state) => state.user)
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'confirmed' | 'paid' | 'delivered'>('all')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [deliveryRequests, setDeliveryRequests] = useState<Record<string, DeliveryRequest>>({})

  useEffect(() => {
    if (user) {
      fetchOrders()
    }
  }, [user, filter, searchQuery])

  const fetchOrders = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      // Create a real-time subscription for order updates
      const orderSubscription = supabase
        .channel('seller-orders')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            filter: `seller_id=eq.${user.id}`
          },
          () => {
            // Refresh orders when changes occur
            fetchOrdersData()
          }
        )
        .subscribe()

      // Initial fetch
      await fetchOrdersData()

      // Cleanup subscription on unmount
      return () => {
        orderSubscription.unsubscribe()
      }
    } catch (err) {
      console.error('Error setting up orders subscription:', err)
    }
  }

  const fetchOrdersData = async () => {
    try {
      let query = supabase
        .from('orders')
        .select(`
          *,
          product:products (
            title,
            images
          ),
          buyer:profiles!buyer_id (
            full_name,
            email,
            phone_number
          )
        `)
        .eq('seller_id', user?.id)
        .in('status', ['confirmed', 'paid', 'delivered'])
        .order('updated_at', { ascending: false })

      if (filter !== 'all') {
        query = query.eq('status', filter)
      }

      if (searchQuery) {
        query = query.or(`product.title.ilike.%${searchQuery}%,id.ilike.%${searchQuery}%`)
      }

      const { data: orders, error: ordersError } = await query
      if (ordersError) throw ordersError
      
      // Fetch delivery requests for all orders
      const { data: requests, error: requestsError } = await supabase
        .from('delivery_requests')
        .select('*')
        .in('order_id', orders?.map(o => o.id) || [])

      if (requestsError) throw requestsError

      // Create a map of order_id to delivery request
      const requestMap = (requests || []).reduce((acc, req) => ({
        ...acc,
        [req.order_id]: req
      }), {})

      setOrders(orders || [])
      setDeliveryRequests(requestMap)
    } catch (err) {
      console.error('Error fetching orders:', err)
      setToastMessage('Erreur lors du chargement des commandes')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const requestDelivery = async (orderId: string) => {
    try {
      const order = orders.find(o => o.id === orderId)
      if (!order) return

      const { error } = await supabase
        .from('delivery_requests')
        .insert([{
          order_id: orderId,
          seller_id: user?.id,
          status: 'pending',
          pickup_instructions: `Récupérer le produit "${order.product.title}" chez le vendeur`,
          delivery_notes: `Livrer à l'adresse: ${order.delivery_address}`
        }])

      if (error) throw error

      setToastMessage('Demande de livraison envoyée avec succès')
      setToastType('success')
      setShowToast(true)
      fetchOrdersData()
    } catch (err) {
      console.error('Error requesting delivery:', err)
      setToastMessage('Erreur lors de la demande de livraison')
      setToastType('error')
      setShowToast(true)
    }
  }

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800'
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'delivered':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4" />
      case 'paid':
        return <DollarSign className="h-4 w-4" />
      case 'delivered':
        return <Truck className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Commande
                </h3>
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Order Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations de la Commande</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-900">ID: {selectedOrder.id}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedOrder.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                        {getStatusIcon(selectedOrder.status)}
                        <span className="ml-1 capitalize">
                          {selectedOrder.status === 'confirmed' ? 'Confirmée' :
                           selectedOrder.status === 'paid' ? 'Payée' :
                           selectedOrder.status === 'delivered' ? 'Livrée' :
                           selectedOrder.status}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Produit</h4>
                  <div className="mt-2 flex items-center">
                    <img
                      src={selectedOrder.product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                      alt={selectedOrder.product.title}
                      className="h-16 w-16 object-cover rounded"
                    />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        {selectedOrder.product.title}
                      </p>
                      <p className="text-sm text-gray-900">
                        Prix final: ${selectedOrder.final_price}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Buyer Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Acheteur</h4>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {selectedOrder.buyer?.full_name || 'Client'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedOrder.buyer?.email}
                        </p>
                      </div>
                    </div>
                    {selectedOrder.buyer?.phone_number && (
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <Phone className="h-4 w-4 mr-2" />
                        {selectedOrder.buyer.phone_number}
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Livraison</h4>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center text-sm text-gray-900">
                      <Truck className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="capitalize">{selectedOrder.delivery_type}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                      {selectedOrder.delivery_address}
                    </div>
                    {selectedOrder.confirmation_code && (
                      <div className="flex items-center text-sm text-gray-500">
                        <CheckCircle className="h-4 w-4 text-gray-400 mr-2" />
                        Code de confirmation: {selectedOrder.confirmation_code}
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Request Status */}
                {deliveryRequests[selectedOrder.id] && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Statut de la Livraison</h4>
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        deliveryRequests[selectedOrder.id].status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : deliveryRequests[selectedOrder.id].status === 'accepted'
                          ? 'bg-blue-100 text-blue-800'
                          : deliveryRequests[selectedOrder.id].status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {deliveryRequests[selectedOrder.id].status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                        {deliveryRequests[selectedOrder.id].status === 'accepted' && <CheckCircle className="h-3 w-3 mr-1" />}
                        {deliveryRequests[selectedOrder.id].status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                        {deliveryRequests[selectedOrder.id].status === 'cancelled' && <XCircle className="h-3 w-3 mr-1" />}
                        {deliveryRequests[selectedOrder.id].status === 'pending' ? 'En attente' :
                         deliveryRequests[selectedOrder.id].status === 'accepted' ? 'Acceptée' :
                         deliveryRequests[selectedOrder.id].status === 'completed' ? 'Terminée' : 'Annulée'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-6 border-t flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowOrderDetails(false)}
                  >
                    Fermer
                  </Button>
                  {selectedOrder.status === 'paid' && !deliveryRequests[selectedOrder.id] && (
                    <Button
                      variant="primary"
                      onClick={() => requestDelivery(selectedOrder.id)}
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      Demander un livreur
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher une commande..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              <option value="all">Tous les statuts</option>
              <option value="confirmed">Confirmées</option>
              <option value="paid">Payées</option>
              <option value="delivered">Livrées</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
            <p className="mt-1 text-sm text-gray-500">
              Vous n'avez pas encore de commandes
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livraison
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="relative px-4 sm:px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <img
                            src={order.product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                            alt={order.product.title}
                            className="h-10 w-10 rounded object-cover"
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 line-clamp-1">
                            {order.product.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            #{order.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-4 w-4 text-gray-500" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {order.buyer?.full_name || 'Client'}
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {order.buyer?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Truck className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="capitalize">{order.delivery_type}</span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="truncate max-w-[150px] sm:max-w-xs">
                          {order.delivery_address}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${order.final_price.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        <span className="ml-1 capitalize">
                          {order.status === 'confirmed' ? 'Confirmée' :
                           order.status === 'paid' ? 'Payée' :
                           order.status === 'delivered' ? 'Livrée' :
                           order.status}
                        </span>
                      </span>
                      {deliveryRequests[order.id] && (
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          deliveryRequests[order.id].status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : deliveryRequests[order.id].status === 'accepted'
                            ? 'bg-blue-100 text-blue-800'
                            : deliveryRequests[order.id].status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          <Truck className="h-3 w-3 mr-1" />
                          {deliveryRequests[order.id].status === 'pending' ? 'Livraison en attente' :
                           deliveryRequests[order.id].status === 'accepted' ? 'Livraison acceptée' :
                           deliveryRequests[order.id].status === 'completed' ? 'Livraison terminée' :
                           'Livraison annulée'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {order.status === 'paid' && !deliveryRequests[order.id] && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => requestDelivery(order.id)}
                        >
                          <Truck className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">Demander un livreur</span>
                          <span className="inline sm:hidden">Livreur</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedOrder(order)
                          setShowOrderDetails(true)
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Détails</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}